import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, Calendar, Phone, Mail, MapPin, ChevronDown,
  Stethoscope, HeartPulse, PawPrint, Smile, Brain, Shield, Briefcase, TrendingUp,
  ArrowRight, Globe
} from 'lucide-react';
import { programs } from '../data/programsData';
import { ALL_STUDY_DESTINATIONS } from '../data/studyDestinationConfigs';
import { useEligibilityModal } from '../context/EligibilityModalContext';

const iconMap = {
  Stethoscope, HeartPulse, PawPrint, Smile, Brain, Shield, Briefcase, TrendingUp
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const [isDestinationsOpen, setIsDestinationsOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const [mobileDestinationsOpen, setMobileDestinationsOpen] = useState(false);
  const location = useLocation();
  const { open: openEligibility } = useEligibilityModal();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProgramsOpen(false);
    setIsDestinationsOpen(false);
    setMobileProgramsOpen(false);
    setMobileDestinationsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/success-stories', label: 'Success Stories' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top utility bar */}
      <div
        className={`bg-[#0B2545] text-white text-xs transition-all duration-300 overflow-hidden ${
          isScrolled ? 'max-h-0 opacity-0' : 'max-h-12 opacity-100'
        }`}
        data-testid="top-utility-bar"
      >
        {/* Desktop layout */}
        <div className="hidden md:flex container mx-auto px-4 items-center justify-between h-9">
          <div className="flex items-center gap-5">
            <a href="tel:+917594881898" className="flex items-center gap-1.5 hover:text-[#4DD4AC] transition-colors">
              <Phone size={12} /> +91 75948 81898
            </a>
            <a href="mailto:info@patrickseducare.com" className="flex items-center gap-1.5 hover:text-[#4DD4AC] transition-colors">
              <Mail size={12} /> info@patrickseducare.com
            </a>
            <span className="flex items-center gap-1.5 text-white/70">
              <MapPin size={12} /> Kerala, India
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 rounded-full bg-[#4DD4AC]/20 text-[#4DD4AC] font-medium">
              ISO 9001:2015 Certified
            </span>
            <a
              href="https://wa.me/917558881898?text=Hi%20Patrick%27s%20Edu%20Care%2C%20I%27d%20like%20to%20know%20more%20about%20studying%20abroad."
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#4DD4AC] transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>

        {/* Mobile compact layout */}
        <div className="md:hidden flex items-center justify-between px-3 h-8 text-[11px]">
          <a
            href="tel:+917594881898"
            className="flex items-center gap-1 hover:text-[#4DD4AC] transition-colors"
            data-testid="mobile-top-phone"
          >
            <Phone size={11} /> +91 75948 81898
          </a>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4DD4AC]/20 text-[#4DD4AC] font-semibold text-[10px]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4DD4AC]" /> ISO 9001:2015
          </span>
        </div>
      </div>

      {/* Main nav (glassy) */}
      <div
        className={`transition-all duration-300 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border-b border-white/40'
            : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'h-16' : 'h-20'}`}>
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="header-logo">
              <img
                src="/img/c9mjttd5_logo.webp"
                alt="Patrick's Edu Care"
                className={`w-auto transition-all duration-300 ${isScrolled ? 'h-10' : 'h-12'}`}
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.slice(0, 2).map((link) => (
                <NavItem key={link.path} link={link} active={location.pathname === link.path} />
              ))}

              {/* Programs mega-dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsProgramsOpen(true)}
                onMouseLeave={() => setIsProgramsOpen(false)}
              >
                <button
                  data-testid="programs-dropdown-trigger"
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname.startsWith('/programs')
                      ? 'text-[#2B5B8A] font-semibold'
                      : 'text-gray-700 hover:text-[#2B5B8A]'
                  }`}
                >
                  Programs <ChevronDown size={14} className={`transition-transform ${isProgramsOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProgramsOpen && (
                  <div
                    data-testid="programs-mega-dropdown"
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[640px] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 animate-in fade-in slide-in-from-top-1 duration-200"
                  >
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45" />
                    <div className="grid grid-cols-2 gap-2 relative">
                      {programs.map((p) => {
                        const Icon = iconMap[p.icon] || Briefcase;
                        return (
                          <Link
                            key={p.slug}
                            to={`/programs/${p.slug}`}
                            data-testid={`mega-program-${p.slug}`}
                            className="group flex items-start gap-3 p-3 rounded-xl hover:bg-[#F0F8FF] transition-colors"
                          >
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${p.color}15` }}
                            >
                              <Icon size={20} style={{ color: p.color }} />
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-[#1A2B3C] text-sm">{p.name}</div>
                              <div className="text-xs text-[#4A5B6C] truncate">
                                {p.countries.length} {p.countries.length === 1 ? 'country' : 'countries'} · {p.duration}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-xs text-[#4A5B6C]">Explore all our globally recognized programs</span>
                      <Link
                        to="/programs"
                        className="text-sm font-semibold text-[#2B5B8A] hover:text-[#4DD4AC] flex items-center gap-1"
                      >
                        View All <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Study Destinations dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsDestinationsOpen(true)}
                onMouseLeave={() => setIsDestinationsOpen(false)}
              >
                <button
                  data-testid="destinations-dropdown-trigger"
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname.startsWith('/study-in-')
                      ? 'text-[#2B5B8A] font-semibold'
                      : 'text-gray-700 hover:text-[#2B5B8A]'
                  }`}
                >
                  Study Destinations <ChevronDown size={14} className={`transition-transform ${isDestinationsOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDestinationsOpen && (
                  <div
                    data-testid="destinations-mega-dropdown"
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[520px] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 animate-in fade-in slide-in-from-top-1 duration-200"
                  >
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45" />
                    <div className="grid grid-cols-2 gap-1.5 relative">
                      {ALL_STUDY_DESTINATIONS.map((d) => (
                        <Link
                          key={d.slug}
                          to={d.href}
                          data-testid={`mega-destination-${d.slug}`}
                          className="group flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#F0F8FF] transition-colors"
                        >
                          <span className="text-xl leading-none">{d.flag}</span>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-[#1A2B3C] text-sm">{d.name}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-xs text-[#4A5B6C]">
                        <Globe size={12} className="inline mr-1" />
                        Guided admissions to 7 destinations
                      </span>
                      <button
                        onClick={() => openEligibility('header-destinations-cta')}
                        className="text-sm font-semibold text-[#2B5B8A] hover:text-[#4DD4AC] flex items-center gap-1"
                      >
                        Check Eligibility <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {navLinks.slice(2).map((link) => (
                <NavItem key={link.path} link={link} active={location.pathname === link.path} />
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-2">
              <button
                onClick={() => openEligibility('header-free-consultation')}
                data-testid="header-free-consultation"
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#2B5B8A] to-[#4DD4AC] hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Free Consultation
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-gray-700 hover:text-[#2B5B8A] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-white border-t border-gray-100 py-3 animate-in slide-in-from-top duration-300 max-h-[calc(100vh-5rem)] overflow-y-auto">
              <nav className="flex flex-col space-y-1">
                {navLinks.slice(0, 2).map((link) => (
                  <MobileNavLink key={link.path} link={link} active={location.pathname === link.path} />
                ))}

                {/* Programs accordion mobile */}
                <button
                  data-testid="mobile-programs-toggle"
                  onClick={() => setMobileProgramsOpen(!mobileProgramsOpen)}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/programs')
                      ? 'text-[#2B5B8A] font-semibold bg-blue-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Programs
                  <ChevronDown size={16} className={`transition-transform ${mobileProgramsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileProgramsOpen && (
                  <div className="bg-[#F8FAFC] py-2">
                    <Link
                      to="/programs"
                      className="block px-8 py-2 text-sm font-semibold text-[#2B5B8A]"
                    >
                      View All Programs →
                    </Link>
                    {programs.map((p) => {
                      const Icon = iconMap[p.icon] || Briefcase;
                      return (
                        <Link
                          key={p.slug}
                          to={`/programs/${p.slug}`}
                          className="flex items-center gap-3 px-8 py-2 text-sm text-gray-700 hover:text-[#2B5B8A] hover:bg-white transition-colors"
                        >
                          <Icon size={16} style={{ color: p.color }} />
                          {p.name}
                        </Link>
                      );
                    })}
                  </div>
                )}

                {/* Study Destinations mobile accordion */}
                <button
                  data-testid="mobile-destinations-toggle"
                  onClick={() => setMobileDestinationsOpen(!mobileDestinationsOpen)}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/study-in-')
                      ? 'text-[#2B5B8A] font-semibold bg-blue-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Study Destinations
                  <ChevronDown size={16} className={`transition-transform ${mobileDestinationsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileDestinationsOpen && (
                  <div className="bg-[#F8FAFC] py-2">
                    {ALL_STUDY_DESTINATIONS.map((d) => (
                      <Link
                        key={d.slug}
                        to={d.href}
                        className="flex items-center gap-3 px-8 py-2 text-sm text-gray-700 hover:text-[#2B5B8A] hover:bg-white transition-colors"
                      >
                        <span className="text-base">{d.flag}</span>
                        <span className="flex-1">{d.name}</span>
                      </Link>
                    ))}
                  </div>
                )}

                {navLinks.slice(2).map((link) => (
                  <MobileNavLink key={link.path} link={link} active={location.pathname === link.path} />
                ))}

                <div className="px-4 pt-3 space-y-2 border-t border-gray-100 mt-2">
                  <a
                    href="tel:+917594881898"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full border border-[#2B5B8A] text-[#2B5B8A] text-sm font-semibold"
                  >
                    <Phone size={16} /> +91 75948 81898
                  </a>
                  <Link
                    to="/contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full text-[#2B5B8A] border border-[#2B5B8A]/30 text-sm font-semibold"
                  >
                    Contact Us
                  </Link>
                  <button
                    onClick={() => { setIsMobileMenuOpen(false); openEligibility('header-mobile-cta'); }}
                    data-testid="mobile-header-free-consultation"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full text-white text-sm font-semibold bg-gradient-to-r from-[#2B5B8A] to-[#4DD4AC]"
                  >
                    Free Consultation
                  </button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const NavItem = ({ link, active }) => (
  <Link
    to={link.path}
    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      active ? 'text-[#2B5B8A] font-semibold' : 'text-gray-700 hover:text-[#2B5B8A]'
    }`}
  >
    {link.label}
  </Link>
);

const MobileNavLink = ({ link, active }) => (
  <Link
    to={link.path}
    className={`px-4 py-2.5 text-sm font-medium transition-colors ${
      active ? 'text-[#2B5B8A] font-semibold bg-blue-50' : 'text-gray-700 hover:bg-gray-50'
    }`}
  >
    {link.label}
  </Link>
);

export default Header;
